import { Controller } from "@hotwired/stimulus"

// Split-button dropdown: the caret segment toggles a small menu of alternate
// actions. Closes on outside click, Escape, or after picking a menu item.
export default class extends Controller {
    // filter/item/group are all optional: only menus that render a filter
    // input (see the Share composer's audience picker) supply them. A menu
    // with no filterTarget just never calls filterOptions/filterKeydown.
    static targets = ["menu", "caret", "filter", "item", "group"]
    // anchor: "fixed" positions the menu with position:fixed under the caret so
    // it can escape a scroll-clipping ancestor (e.g. the horizontally scrolling
    // message-filter row). Default anchoring stays CSS-absolute.
    static values = { anchor: String }

    connect() {
        this.outsideClick = (event) => {
            if (!this.element.contains(event.target)) this.close()
        }
        this.keydown = (event) => {
            if (event.key === "Escape") this.close()
        }
        this.reposition = () => (this.anchorValue === "fixed" ? this.positionMenu() : this.clampMenu())
    }

    disconnect() {
        this.close()
    }

    toggle(event) {
        event.preventDefault()
        if (this.menuTarget.classList.contains("is-open")) {
            this.close()
        } else {
            this.open()
        }
    }

    open() {
        this.menuTarget.classList.add("is-open")
        // Optional top-layer panels escape frosted/scrolling sidebars too.
        if (this.menuTarget.hasAttribute("popover")) this.menuTarget.showPopover()
        this.caretTarget.setAttribute("aria-expanded", "true")
        document.addEventListener("click", this.outsideClick)
        document.addEventListener("keydown", this.keydown)
        // A fixed panel is placed outright; a CSS-anchored one keeps its own
        // alignment and is only nudged back inside the viewport. Scroll matters
        // to the fixed case alone — that panel doesn't follow its trigger,
        // while an absolutely positioned one moves with it.
        if (this.anchorValue === "fixed") {
            this.positionMenu()
            window.addEventListener("scroll", this.reposition, true)
        } else {
            this.clampMenu()
        }
        window.addEventListener("resize", this.reposition)
        this._watchMenuSize()
        // Land the cursor in the filter box so typing narrows the list right
        // away — the whole point of adding it was to skip the pointer.
        if (this.hasFilterTarget) this.filterTarget.focus()
    }

    close() {
        if (this.hasMenuTarget) {
            this.menuTarget.classList.remove("is-open")
            if (this.menuTarget.hasAttribute("popover") && this.menuTarget.matches(":popover-open")) this.menuTarget.hidePopover()
        }
        if (this.hasCaretTarget) this.caretTarget.setAttribute("aria-expanded", "false")
        document.removeEventListener("click", this.outsideClick)
        document.removeEventListener("keydown", this.keydown)
        window.removeEventListener("resize", this.reposition)
        window.removeEventListener("scroll", this.reposition, true)
        this._unwatchMenuSize()
        if (this.hasMenuTarget) this.menuTarget.style.translate = ""
        this._resetFilter()
    }

    // Both placements measure the panel, and the size they measure at open time
    // is not always the size it settles at. The family tree's filter ships its
    // count badges empty (display:none) and fills them once the tree payload
    // lands, so a menu opened before then was clamped at its narrow width and
    // then grew ~30px past the right edge of a phone. Re-place it whenever its
    // box changes while open. The work is deferred a frame so a placement that
    // itself changes the size (a fixed panel moved left gains room to unwrap)
    // lands on the next observation instead of tripping the observer's
    // same-frame loop guard.
    _watchMenuSize() {
        if (!this.hasMenuTarget || typeof ResizeObserver !== "function") return
        this._unwatchMenuSize()
        this.menuResizes = new ResizeObserver(() => {
            cancelAnimationFrame(this.menuResizeFrame)
            this.menuResizeFrame = requestAnimationFrame(() => {
                if (this.hasMenuTarget && this.menuTarget.classList.contains("is-open")) this.reposition()
            })
        })
        this.menuResizes.observe(this.menuTarget)
    }

    _unwatchMenuSize() {
        cancelAnimationFrame(this.menuResizeFrame)
        this.menuResizes?.disconnect()
        this.menuResizes = null
    }

    // Type-to-narrow: matches each item's data-label (falls back to its text)
    // against the query, then hides a group entirely once none of its items
    // match so an empty "GROUPS" header never lingers.
    filterOptions() {
        if (!this.hasFilterTarget) return
        const query = this.filterTarget.value.trim().toLowerCase()
        this.itemTargets.forEach((item) => {
            const text = (item.dataset.label || item.textContent || "").trim().toLowerCase()
            item.hidden = query.length > 0 && !text.includes(query)
        })
        this.groupTargets.forEach((group) => {
            const anyVisible = Array.from(group.querySelectorAll(".assistant-header-menu-item")).some((item) => !item.hidden)
            group.hidden = query.length > 0 && !anyVisible
        })
    }

    // Enter commits to whatever the filter narrowed down to, without
    // requiring a pointer to reach the row.
    filterKeydown(event) {
        if (event.key !== "Enter") return
        event.preventDefault()
        const firstVisible = this.itemTargets.find((item) => !item.hidden)
        if (firstVisible) firstVisible.click()
    }

    _resetFilter() {
        if (!this.hasFilterTarget) return
        this.filterTarget.value = ""
        this.filterOptions()
    }

    // Pin the menu just under the split chip in viewport coordinates, so an
    // overflow-clipping scroll ancestor can't hide it. Left-aligned to the host,
    // then clamped inside the viewport — a host near the right edge (a card menu
    // in the last column) would otherwise push the panel off screen.
    positionMenu() {
        if (!this.hasMenuTarget) return
        const host = this.element.getBoundingClientRect()
        const menu = this.menuTarget
        menu.style.position = "fixed"
        menu.style.right = "auto"
        menu.style.bottom = "auto"

        const margin = 8
        const width = menu.offsetWidth || 0
        const maxLeft = Math.max(margin, window.innerWidth - width - margin)
        const left = Math.round(Math.min(Math.max(host.left, margin), maxLeft))

        // Flip above the trigger when there isn't room below it. Without this a
        // menu on the last row of a long list (the admin user roster) opened
        // straight off the bottom of the viewport, and a fixed panel can't be
        // scrolled back into view the way an in-flow one can. Only flips when
        // the space above is genuinely better, so a short viewport that fits
        // neither still opens downward — the top of the menu is where its first
        // item is.
        const height = menu.offsetHeight || 0
        const below = window.innerHeight - host.bottom - margin
        const above = host.top - margin
        const top = height > below && above > below ? Math.round(Math.max(margin, host.top - 6 - height)) : Math.round(host.bottom + 6)

        // left/top above are viewport coordinates, but `position: fixed` is only
        // viewport-relative when no ancestor has a transform, filter or
        // backdrop-filter. .assistant-phone keeps its frosted backdrop-filter in
        // dark mode, and in a narrow desktop window it is inset 16px/32px by the
        // shell's padding — so every fixed menu landed that far right and down,
        // past the viewport edge the clamp had just kept it inside. Place, see
        // where the box really went, and take the difference back out.
        menu.style.left = `${left}px`
        menu.style.top = `${top}px`
        const placed = menu.getBoundingClientRect()
        const dx = Math.round(placed.left - left)
        const dy = Math.round(placed.top - top)
        if (dx !== 0) menu.style.left = `${left - dx}px`
        if (dy !== 0) menu.style.top = `${top - dy}px`
    }

    // Nudge a CSS-anchored panel back inside the viewport. `.network-toolbar-menu-panel`
    // is right-aligned to its trigger, which drops it off the LEFT edge as soon as the
    // trigger sits closer to that edge than the panel is wide: the Tasks toolbar's
    // funnel at phone width, held ~240px clear of the right edge by the five round
    // buttons after it, opened at x = -36 and clipped the first characters of every row
    // label ("Normal" read as "rmal"). Shifting with `translate` preserves whatever
    // alignment the CSS chose — right-aligned here, `right: auto` for a panel that
    // borrows Bootstrap's .dropdown-menu — and leaves `transform` free.
    clampMenu() {
        if (!this.hasMenuTarget) return
        const menu = this.menuTarget
        menu.style.translate = ""
        const margin = 8
        const rect = menu.getBoundingClientRect()
        // No layout box (a hidden ancestor, a zero-size window) means nothing to
        // measure against — shifting on those numbers just invents an offset.
        if (rect.width === 0 || window.innerWidth === 0) return
        const overflowLeft = margin - rect.left
        const overflowRight = rect.right - (window.innerWidth - margin)
        let shift = 0
        if (overflowLeft > 0) {
            shift = overflowLeft
        } else if (overflowRight > 0) {
            // A panel wider than the viewport can't clear both edges; keep the left one
            // on screen, since that is where the labels start.
            shift = -Math.min(overflowRight, rect.left - margin)
        }
        if (shift !== 0) menu.style.translate = `${Math.round(shift)}px`
    }

    choose() {
        this.close()
    }
}
